import React from 'react';
import { ConfirmationModal } from '@app/react-common/components/confirmation-modal/ConfirmationModal';
import { AbTestModal } from '../ab-test-modal/AbTestModal';
import { AbTestModalV2 } from '../ab-test-modal/AbTestModalV2';
import { WarningBanner } from '@app/react-common/components/banners/WarningBanner';
import { AbTest } from '../../models/AbTest';
import { IAbTestActions } from '../../models/AbTestActions';
import { ModalStates, ModalActions, WinnerModalState } from './hooks/useAbTestModal';
import {
  getAbTestType,
  getAbTestTypeCategory,
  getCheckoutProfileWinnerDialogText,
  getSetWinnerDialogText,
} from '../../utils/AbTestingUtils';
import { Checkout } from '../../../../checkout/react/models/Checkout';
import useShop from '@app/react-common/hooks/useShop';
import { AbTestWidgetType } from '../../models/AbTestCreationType';
import { AbTestTabTypeLabel } from '../../models/AbTestType';
import {
  getIsCheckoutProfileTest,
  getIsThemeTest,
  getIsWinnerAppliedInShopify,
  getWinnerButtonText,
  shouldUseAbTestModalV2,
} from '../../utils';
import { ThemeWinnerSuccessContent } from './ThemeWinnerSuccessContent';
import { PdpWinnerSuccessContent } from './PdpWinnerSuccessContent';
import { AbTestTypeCategory } from '../../models/AbTestCreationType';

interface AbTestModalsProps {
  abTest: AbTest;
  abTestActions: IAbTestActions;
  modalStates: ModalStates;
  modalActions: ModalActions;
  runningAbTestCheckoutIds: number[];
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  checkouts: Checkout[];
  setAsWinnerOpen: WinnerModalState;
  setSetAsWinnerOpen: React.Dispatch<React.SetStateAction<WinnerModalState>>;
  forceSetAsWinnerOpen: WinnerModalState;
  setForceSetAsWinnerOpen: React.Dispatch<React.SetStateAction<WinnerModalState>>;
  angularRedirect: (url: string) => void;
}

const createAbTestDuplicate = (abTest: AbTest): AbTest => {
  return {
    ...abTest,
    id: undefined,
    startedAt: undefined,
    endedAt: undefined,
    archivedAt: undefined,
    createdAt: undefined,
    testComments: undefined,
    winnerAt: undefined,
    name: `Copy - ${abTest.name}`,
    abTestVariants: abTest.abTestVariants.map((variant) => ({
      ...variant,
      id: undefined,
      abTestWidgets: variant.abTestWidgets.map((widget) => ({ ...widget, id: undefined })),
    })),
  };
};

export default function AbTestModals({
  abTest,
  abTestActions,
  modalStates,
  modalActions,
  runningAbTestCheckoutIds,
  setActiveTab,
  checkouts,
  setAsWinnerOpen,
  setSetAsWinnerOpen,
  forceSetAsWinnerOpen,
  setForceSetAsWinnerOpen,
  angularRedirect,
}: AbTestModalsProps) {
  const shop = useShop();
  const useAbTestModalV2 = shouldUseAbTestModalV2(shop?.metadata, abTest.testType, abTest.mode);

  const winnerVariant = abTest.abTestVariants?.find(
    (v) => v.id === setAsWinnerOpen.id || v.id === forceSetAsWinnerOpen.id,
  );
  const winnerCheckout =
    winnerVariant &&
    checkouts.find((x) => x.id == winnerVariant.abTestWidgets?.find((y) => y.type == 'checkout')?.externalId);

  // For cross-site and product page tests, use variant name; for checkout tests, use checkout name
  const isThemeTest = getIsThemeTest(abTest.testType);
  const isPdpTest = abTest.testType === AbTestTypeCategory.PRODUCT_PAGE;
  const isCrossSiteTest = abTest.testType === AbTestTypeCategory.CROSS_SITE;
  const isCheckoutProfileTest = getIsCheckoutProfileTest(abTest.testType);
  const isWinnerAppliedInShopify = getIsWinnerAppliedInShopify(abTest.testType);
  const winnerName = isWinnerAppliedInShopify ? winnerVariant?.name : winnerCheckout?.name;

  return (
    <>
      {modalStates.start && (
        <ConfirmationModal
          title="Start A/B test?"
          buttonText="Start test"
          mainContent={`You are about to start test "${abTest.name}"`}
          isOpen={modalStates.start}
          onClose={() => modalActions.close('start')}
          onSuccess={async () => {
            await abTestActions.startTest(abTest.id);
            modalActions.close('start');
          }}
        />
      )}

      {modalStates.delete && (
        <ConfirmationModal
          title="Are you sure?"
          buttonText="Delete"
          mainContent={`You're deleting the A/B test ${abTest.name}`}
          isOpen={modalStates.delete}
          onClose={() => modalActions.close('delete')}
          onSuccess={async () => {
            await abTestActions.deleteTest(abTest.id, getAbTestType(abTest));
            modalActions.close('delete');
          }}
        />
      )}

      {modalStates.end && (
        <ConfirmationModal
          title="End A/B test?"
          buttonText="End test"
          mainContent={`You're ending A/B test "${abTest.name}"`}
          isOpen={modalStates.end}
          onClose={() => modalActions.close('end')}
          onSuccess={async () => {
            await abTestActions.endTest(abTest.id);
            modalActions.close('end');
          }}
        />
      )}

      {modalStates.archive && (
        <ConfirmationModal
          title="Archiving AB Test"
          buttonText="Confirm"
          mainContent="Archiving an AB Test will make it inaccessible for users within PDQ. Click 'confirm' to proceed."
          isOpen={modalStates.archive}
          onClose={() => modalActions.close('archive')}
          onSuccess={async () => {
            await abTestActions.archiveTest(abTest.id);
            modalActions.close('archive');
          }}
        />
      )}

      {modalStates.edit &&
        (useAbTestModalV2 ? (
          <AbTestModalV2
            isOpen={modalStates.edit}
            existingAbTest={abTest}
            onClose={() => modalActions.close('edit')}
            onSaveSuccess={() => modalActions.close('edit')}
            saveTest={abTestActions.upsertTest}
            mode="edit"
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ) : (
          <AbTestModal
            isOpen={modalStates.edit}
            existingAbTest={abTest}
            onClose={() => modalActions.close('edit')}
            onSaveSuccess={() => modalActions.close('edit')}
            saveTest={abTestActions.upsertTest}
            mode="edit"
            testTypeCategory={getAbTestTypeCategory(abTest)}
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ))}

      {modalStates.view &&
        (useAbTestModalV2 ? (
          <AbTestModalV2
            isOpen={modalStates.view}
            existingAbTest={abTest}
            onClose={() => modalActions.close('view')}
            onSaveSuccess={() => modalActions.close('view')}
            mode="view"
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ) : (
          <AbTestModal
            isOpen={modalStates.view}
            existingAbTest={abTest}
            onClose={() => modalActions.close('view')}
            onSaveSuccess={() => modalActions.close('view')}
            mode="view"
            testTypeCategory={getAbTestTypeCategory(abTest)}
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ))}

      {modalStates.duplicate &&
        (useAbTestModalV2 ? (
          <AbTestModalV2
            isOpen={modalStates.duplicate}
            existingAbTest={createAbTestDuplicate(abTest)}
            onClose={() => modalActions.close('duplicate')}
            onSaveSuccess={() => {
              modalActions.close('duplicate');
              setActiveTab(AbTestTabTypeLabel.DRAFTS);
            }}
            saveTest={abTestActions.upsertTest}
            mode="duplicate"
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ) : (
          <AbTestModal
            isOpen={modalStates.duplicate}
            existingAbTest={createAbTestDuplicate(abTest)}
            onClose={() => modalActions.close('duplicate')}
            onSaveSuccess={() => {
              modalActions.close('duplicate');
              setActiveTab(AbTestTabTypeLabel.DRAFTS);
            }}
            saveTest={abTestActions.upsertTest}
            mode="duplicate"
            testTypeCategory={getAbTestTypeCategory(abTest)}
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          />
        ))}

      {setAsWinnerOpen.show && winnerName && (
        <ConfirmationModal
          title={`${getWinnerButtonText(abTest.testType, true)} "${winnerName}" as winner`}
          buttonText={getWinnerButtonText(abTest.testType)}
          mainContent={
            isPdpTest ? (
              <PdpWinnerSuccessContent />
            ) : isCrossSiteTest ? (
              <ThemeWinnerSuccessContent />
            ) : isCheckoutProfileTest ? (
              getCheckoutProfileWinnerDialogText()
            ) : (
              getSetWinnerDialogText()
            )
          }
          isOpen={setAsWinnerOpen.show}
          onClose={() => setSetAsWinnerOpen({ show: false, id: undefined })}
          onSuccess={async () => {
            const message = await abTestActions.setWinner(setAsWinnerOpen.id!);
            if (message.length > 0) {
              setForceSetAsWinnerOpen({ show: true, id: setAsWinnerOpen.id, text: message });
              setSetAsWinnerOpen({ show: false, id: undefined });
              return;
            }
            setSetAsWinnerOpen({ show: false, id: undefined });
            // For checkout tests, redirect to checkout groups; for cross-site/product page, skip redirect
            if (!isWinnerAppliedInShopify) {
              angularRedirect(
                `/convert/checkout-groups?active=${winnerVariant!.abTestWidgets.find((x) => x.type == AbTestWidgetType.CHECKOUT)!.externalId}`,
              );
            }
          }}
        />
      )}

      {forceSetAsWinnerOpen.show && winnerName && (
        <ConfirmationModal
          title={`${getWinnerButtonText(abTest.testType, true)} "${winnerName}" as winner`}
          buttonText={getWinnerButtonText(abTest.testType)}
          mainContent={<WarningBanner title={forceSetAsWinnerOpen.text || ''} content="" hideCloseButton={true} />}
          isOpen={forceSetAsWinnerOpen.show}
          disableConfirmation={forceSetAsWinnerOpen.text?.includes('Operation is blocked')}
          onClose={() => setForceSetAsWinnerOpen({ show: false, id: undefined, text: undefined })}
          onSuccess={async () => {
            await abTestActions.setWinnerForce(forceSetAsWinnerOpen.id!);
            setForceSetAsWinnerOpen({ show: false, id: undefined, text: '' });
            // For checkout tests, redirect to checkout groups; for cross-site/product page, skip redirect
            if (!isWinnerAppliedInShopify) {
              angularRedirect(
                `/convert/checkout-groups?active=${winnerVariant!.abTestWidgets.find((x) => x.type == 'checkout')!.externalId}`,
              );
            }
          }}
        />
      )}

      {modalStates.setWinnerInstructions && isThemeTest && (
        <ConfirmationModal
          title="Set Winner Instructions"
          buttonText="Got it"
          mainContent={isPdpTest ? <PdpWinnerSuccessContent /> : <ThemeWinnerSuccessContent />}
          isOpen={modalStates.setWinnerInstructions}
          onClose={() => modalActions.close('setWinnerInstructions')}
          onSuccess={() => modalActions.close('setWinnerInstructions')}
          hideCancel={true}
        />
      )}
    </>
  );
}
